import type { Task, TaskAssigneeSnapshot } from '~/types/models';

export type TaskAssigneeOption = {
    id: string,
    label: string,
    avatarURL?: string,
}

export function resolveTaskAssigneeIds(task: Partial<Task> | null | undefined): string[] {
    if (!task) return [];

    const ids = Array.isArray(task.assigneeIds) ? task.assigneeIds : [];
    if (ids.length > 0) {
        return ids.map(id => String(id || '').trim()).filter(Boolean);
    }

    const single = String(task.assigneeId || '').trim();
    return single ? [single] : [];
}

export function resolveTaskAssignees(
    task: Partial<Task> | null | undefined,
    options: TaskAssigneeOption[] = [],
): TaskAssigneeSnapshot[] {
    if (!task) return [];

    if (Array.isArray(task.assignees) && task.assignees.length > 0) {
        return task.assignees
            .map((assignee) => ({
                id: String(assignee.id || '').trim(),
                label: String(assignee.label || '').trim(),
                avatarURL: assignee.avatarURL,
            }))
            .filter((assignee) => assignee.id && assignee.label);
    }

    const ids = resolveTaskAssigneeIds(task);
    if (ids.length > 0) {
        return ids.map((id) => {
            const option = options.find(entry => entry.id === id);
            return {
                id,
                label: option?.label || (ids.length === 1 ? String(task.assignee || '').trim() : id),
                avatarURL: option?.avatarURL,
            };
        }).filter((assignee) => assignee.label);
    }

    const singleLabel = String(task.assignee || '').trim();
    const singleId = String(task.assigneeId || '').trim();
    if (singleLabel || singleId) {
        return [{
            id: singleId || singleLabel,
            label: singleLabel || singleId,
        }];
    }

    return [];
}

export function buildTaskAssigneePayload(ids: string[], options: TaskAssigneeOption[]) {
    const normalizedIds = ids.map(id => String(id || '').trim()).filter(Boolean);
    const assignees = normalizedIds.map((id) => {
        const option = options.find(entry => entry.id === id);
        return {
            id,
            label: option?.label || id,
            ...(option?.avatarURL ? { avatarURL: option.avatarURL } : {}),
        } satisfies TaskAssigneeSnapshot;
    });

    return {
        assigneeIds: normalizedIds,
        assignees,
        assigneeId: normalizedIds[0] || '',
        assignee: assignees[0]?.label || '',
    };
}
